// A write that can tell "saved" from "RLS said no" (HRP-V1).
//
// An UPDATE whose row the policy filters out is not an error to PostgREST: it
// updates zero rows and answers 204 with `error: null`. PeopleHRScreen's
// persist() read that as success, so a lead (people:view) retitled a teammate,
// saw the new title in the header and the roster, and lost it on reload — the
// profiles UPDATE policy had matched nothing. Asking for the written rows back
// (`.select(<key>)`) makes the refusal visible: an empty array.
//
//   const r = await write(supabase.from('profiles').update(delta).eq('id', id));
//   if (!r.ok) { revert(); show(r.reason === 'refused' ? 'You can’t change this' : r.error.message); }
//
// No caller uses it yet; Phase 3 moves persist() and the other optimistic
// writes onto it.
//
// `returning` names a column the row HAS and the writer can READ: the
// employee_bank / _contact / _compensation tables key on `profile_id` and have
// no `id`. Readable, because RETURNING also passes through the SELECT policy —
// a write the caller may make but not read back never reports ok. No table has
// that shape of policy today; one that adds it needs a different check.

/** The part of a PostgREST error a caller shows or reports. */
export type WriteError = { message: string; code?: string; hint?: string | null };

export type WriteResult =
  | { ok: true }
  | { ok: false; reason: 'refused' }
  | { ok: false; reason: 'error'; error: WriteError };

/** An update/upsert/delete builder, before it is awaited. */
type Returnable = {
  select: (columns: string) => PromiseLike<{ data: unknown; error: WriteError | null }>;
};

export const write = async (query: Returnable, returning = 'id'): Promise<WriteResult> => {
  const { data, error } = await query.select(returning);
  if (error) return { ok: false, reason: 'error', error };
  return Array.isArray(data) && data.length > 0 ? { ok: true } : { ok: false, reason: 'refused' };
};
